import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

// @ts-expect-error TS2724
import type { PublicationModel, PublicationRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['publications']

type PublicationFilters = {
  provider_assigned_brand_id?: number,
  provider_id?: number,
  provider_ids?: number[],
  string?: string,
}

type UsePublicationsOptions = {
  filters?: PublicationFilters,
  performHttpRequests?: boolean,
  requestOptions?: PublicationRequestOptions,
}

function usePublications(options: UsePublicationsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_assigned_brand_id: filterProviderAssignedBrandId,
    provider_id: filterProviderId,
    provider_ids: filterProviderIds,
    string,
  } = filters

  const {
    updatedEntities: { publications: publicationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { publications } = useSelector(reduxState => reduxState.entities)

  const filteredPublications = useMemo(() => {
    const filtered = Object.values(publications).filter((publication: PublicationModel) => {
      const {
        id, provider_assigned_brand_ids = [], provider_id, title,
      } = publication

      const matchBrand = matchFilterArrayIncludes(provider_assigned_brand_ids, filterProviderAssignedBrandId)
      const matchProvider = matchFilterNumber(provider_id, filterProviderId)
      const matchProviders = filterProviderIds ? matchFilterArrayIncludes(filterProviderIds, provider_id) : true
      const matchString = matchFilterString(title, string) || matchFilterString(id.toString(), string)

      return matchBrand && matchProvider && matchProviders && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [publicationsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationsCount = filteredPublications.length
  const hasFilteredPublications = !!filteredPublicationsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPublications } = useReduxAction(
    'publications',
    'loadPublications',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublications,
    filteredPublicationsCount,
    hasFilteredPublications,
    loading: loadingPublications,
  }
}

export default usePublications
